/**
 * Sticky Add-to-Cart bar - Dawn Custom Element pattern
 * Syncs image, price, variant text with product form selection
 */
class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('[data-sticky-atc-button]');
    this.submitButton = document.querySelector('product-form .product-form__submit');
    this.imgEl = this.querySelector('[data-sticky-img]');
    this.priceEl = this.querySelector('[data-sticky-price]');
    this.variantEl = this.querySelector('[data-sticky-variant]');

    if (!this.button || !this.submitButton) return;

    this.originalText = this.button.textContent;
    this.button.addEventListener('click', () => {
      this.submitButton.click();
      // Visual feedback
      this.button.textContent = 'Added!';
      this.button.style.opacity = '0.7';
      setTimeout(() => {
        this.button.textContent = this.originalText;
        this.button.style.opacity = '';
      }, 1500);
    });

    // Show/hide on scroll past buy button
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );
    this.observer.observe(this.submitButton);

    // Listen for variant changes
    this.listenVariantChange();
  }

  listenVariantChange() {
    // Dawn PubSub
    if (window.subscribe && window.PUB_SUB_EVENTS?.variantChange) {
      window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant;
        if (variant) this.updateVariant(variant);
      });
    }

    // Fallback: observe hidden variant input
    const variantInput = document.querySelector('product-form input.product-variant-id');
    if (variantInput) {
      const obs = new MutationObserver(() => this.fetchVariant(variantInput.value));
      obs.observe(variantInput, { attributes: true, attributeFilter: ['value'] });
    }
  }

  async fetchVariant(variantId) {
    try {
      const handle = this.dataset.productHandle;
      if (!handle) return;
      const r = await fetch(`${window.shopUrl}/products/${handle}.js`);
      const product = await r.json();
      const variant = product.variants.find(v => String(v.id) === String(variantId));
      if (variant) this.updateVariant(variant);
    } catch {}
  }

  updateVariant(variant) {
    // Update image
    if (this.imgEl && variant.featured_image) {
      this.imgEl.src = variant.featured_image.src
        ? variant.featured_image.src.replace(/(\.[^.]+)$/, '_80x80$1')
        : variant.featured_image;
    }

    // Update price
    if (this.priceEl) {
      const compareHtml = variant.compare_at_price && variant.compare_at_price > variant.price
        ? `<s class="sticky-atc__compare-price">${this.formatMoney(variant.compare_at_price)}</s> `
        : '';
      this.priceEl.innerHTML = compareHtml + this.formatMoney(variant.price);
    }

    // Update variant text (e.g. "T-Shirt | Navy / M")
    if (this.variantEl) {
      this.variantEl.textContent = variant.title !== 'Default Title' ? variant.title : '';
    }

    // Update button state
    if (variant.available === false) {
      this.button.disabled = true;
      this.button.textContent = window.variantStrings?.soldOut || 'Sold Out';
    } else {
      this.button.disabled = false;
      this.button.textContent = window.variantStrings?.addToCart || 'Add to cart';
    }
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return '$' + (cents / 100).toFixed(2);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
